"use client"



// Dot matrix style icons - each icon is made of dots
export interface DotIconProps {
  name:
    | "mic"
    | "mic-off"
    | "camera"
    | "camera-off"
    | "screen"
    | "file"
    | "image"
    | "send"
    | "stop"
    | "close"
    | "menu"
    | "plus"
    | "check"
    | "download"
    | "play"
    | "pause"
    | "settings"
    | "moon"
    | "sun"
    | "phone"
    | "sparkle"
    | "search"
    | "doc"
    | "bolt"
    | "mail"
    | "trash"
    | "screen-off"
    | "waveform"
    | "user"
    | "calendar"
    | "maximize"
    | "chevron-down"
    | "chevron-right"
    | "clock"
    | "message"
    | "copy"
    | "external-link"
    | "shield-check"
    | "shield-alert"
    | "building"
    | "check-circle"
    | "alert-circle"
    | "dollar"
    | "target"
    | "swords"
    | "quote"
    | "alert"
    | "wifi-off"
    | "refresh"
    | "help"
    | "chevron-up"
    | "map-pin"
    | "upload"
    | "globe"
    | "info"
    | "grid"
    | "cpu"
    | "hash"
    | "wrench"
    | "flip"
  size?: number
  className?: string
  animate?: boolean
}

export function DotIcon({ name, size = 20, className = "", animate = false }: DotIconProps) {
  const dotSize = Math.max(1, size / 20)
  const gap = dotSize * 2.5

  // Define icons as dot matrices (5x5 grid)
  const icons: Record<string, number[][]> = {
    mic: [
      [0, 1, 1, 1, 0],
      [0, 1, 1, 1, 0],
      [0, 1, 1, 1, 0],
      [0, 0, 1, 0, 0],
      [1, 1, 1, 1, 1],
    ],
    "mic-off": [
      [1, 1, 1, 1, 0],
      [0, 1, 1, 0, 1],
      [0, 1, 0, 1, 0],
      [0, 0, 1, 0, 0],
      [1, 1, 1, 1, 1],
    ],
    camera: [
      [0, 0, 1, 0, 0],
      [1, 1, 1, 1, 1],
      [1, 0, 1, 0, 1],
      [1, 0, 1, 0, 1],
      [1, 1, 1, 1, 1],
    ],
    "camera-off": [
      [1, 0, 1, 0, 0],
      [0, 1, 1, 1, 1],
      [1, 0, 1, 0, 1],
      [1, 0, 0, 1, 1],
      [1, 1, 1, 1, 1],
    ],
    screen: [
      [1, 1, 1, 1, 1],
      [1, 0, 0, 0, 1],
      [1, 0, 0, 0, 1],
      [1, 1, 1, 1, 1],
      [0, 0, 1, 0, 0],
    ],
    file: [
      [1, 1, 1, 0, 0],
      [1, 0, 1, 1, 0],
      [1, 0, 0, 0, 1],
      [1, 0, 0, 0, 1],
      [1, 1, 1, 1, 1],
    ],
    image: [
      [1, 1, 1, 1, 1],
      [1, 1, 0, 0, 1],
      [1, 0, 1, 0, 1],
      [1, 0, 0, 1, 1],
      [1, 1, 1, 1, 1],
    ],
    send: [
      [0, 0, 1, 0, 0],
      [0, 0, 1, 1, 0],
      [1, 1, 1, 1, 1],
      [0, 0, 1, 1, 0],
      [0, 0, 1, 0, 0],
    ],
    stop: [
      [1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1],
    ],
    close: [
      [1, 0, 0, 0, 1],
      [0, 1, 0, 1, 0],
      [0, 0, 1, 0, 0],
      [0, 1, 0, 1, 0],
      [1, 0, 0, 0, 1],
    ],
    menu: [
      [0, 0, 0, 0, 0],
      [1, 1, 1, 1, 1],
      [0, 0, 0, 0, 0],
      [1, 1, 1, 1, 1],
      [0, 0, 0, 0, 0],
    ],
    plus: [
      [0, 0, 1, 0, 0],
      [0, 0, 1, 0, 0],
      [1, 1, 1, 1, 1],
      [0, 0, 1, 0, 0],
      [0, 0, 1, 0, 0],
    ],
    check: [
      [0, 0, 0, 0, 1],
      [0, 0, 0, 1, 0],
      [1, 0, 1, 0, 0],
      [0, 1, 0, 0, 0],
      [0, 0, 0, 0, 0],
    ],
    download: [
      [0, 0, 1, 0, 0],
      [0, 0, 1, 0, 0],
      [1, 0, 1, 0, 1],
      [0, 1, 1, 1, 0],
      [1, 1, 1, 1, 1],
    ],
    play: [
      [1, 0, 0, 0, 0],
      [1, 1, 0, 0, 0],
      [1, 1, 1, 0, 0],
      [1, 1, 0, 0, 0],
      [1, 0, 0, 0, 0],
    ],
    pause: [
      [1, 1, 0, 1, 1],
      [1, 1, 0, 1, 1],
      [1, 1, 0, 1, 1],
      [1, 1, 0, 1, 1],
      [1, 1, 0, 1, 1],
    ],
    settings: [
      [0, 1, 0, 1, 0],
      [1, 1, 1, 1, 1],
      [0, 1, 1, 1, 0],
      [1, 1, 1, 1, 1],
      [0, 1, 0, 1, 0],
    ],
    moon: [
      [0, 0, 1, 1, 0],
      [0, 1, 0, 0, 0],
      [1, 0, 0, 0, 0],
      [0, 1, 0, 0, 0],
      [0, 0, 1, 1, 0],
    ],
    sun: [
      [0, 0, 1, 0, 0],
      [1, 0, 1, 0, 1],
      [0, 1, 1, 1, 0],
      [1, 0, 1, 0, 1],
      [0, 0, 1, 0, 0],
    ],
    phone: [
      [1, 1, 0, 0, 0],
      [0, 1, 0, 0, 0],
      [0, 0, 1, 0, 0],
      [0, 0, 0, 1, 0],
      [0, 0, 0, 1, 1],
    ],
    sparkle: [
      [0, 0, 1, 0, 0],
      [0, 1, 1, 1, 0],
      [1, 1, 1, 1, 1],
      [0, 1, 1, 1, 0],
      [0, 0, 1, 0, 0],
    ],
    search: [
      [0, 1, 1, 1, 0],
      [1, 0, 0, 0, 1],
      [1, 0, 0, 0, 1],
      [0, 1, 1, 1, 0],
      [0, 0, 0, 1, 1],
    ],
    doc: [
      [1, 1, 1, 1, 1],
      [1, 0, 0, 0, 1],
      [1, 1, 1, 0, 1],
      [1, 0, 0, 0, 1],
      [1, 1, 1, 1, 1],
    ],
    bolt: [
      [0, 0, 1, 1, 0],
      [0, 1, 1, 0, 0],
      [1, 1, 1, 1, 0],
      [0, 0, 1, 1, 0],
      [0, 1, 1, 0, 0],
    ],
    mail: [
      [1, 1, 1, 1, 1],
      [1, 1, 0, 1, 1],
      [1, 0, 1, 0, 1],
      [1, 0, 0, 0, 1],
      [1, 1, 1, 1, 1],
    ],
    trash: [
      [0, 1, 1, 1, 0],
      [1, 1, 1, 1, 1],
      [1, 0, 1, 0, 1],
      [1, 0, 1, 0, 1],
      [1, 1, 1, 1, 1],
    ],
    "screen-off": [
      [1, 1, 1, 1, 1],
      [1, 0, 0, 0, 1],
      [1, 0, 1, 0, 1],
      [1, 0, 0, 0, 1],
      [1, 1, 1, 1, 1],
    ],
    waveform: [
      [0, 0, 1, 0, 0],
      [0, 1, 1, 1, 0],
      [1, 1, 1, 1, 1],
      [0, 1, 1, 1, 0],
      [0, 0, 1, 0, 0],
    ],
    user: [
      [0, 1, 1, 1, 0],
      [0, 1, 1, 1, 0],
      [0, 0, 0, 0, 0],
      [1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1],
    ],
    calendar: [
      [0, 1, 0, 1, 0],
      [1, 1, 1, 1, 1],
      [1, 0, 0, 0, 1],
      [1, 0, 1, 0, 1],
      [1, 1, 1, 1, 1],
    ],
    maximize: [
      [1, 1, 0, 1, 1],
      [1, 0, 0, 0, 1],
      [0, 0, 0, 0, 0],
      [1, 0, 0, 0, 1],
      [1, 1, 0, 1, 1],
    ],
    "chevron-down": [
      [0, 0, 0, 0, 0],
      [1, 0, 0, 0, 1],
      [0, 1, 0, 1, 0],
      [0, 0, 1, 0, 0],
      [0, 0, 0, 0, 0],
    ],
    "chevron-right": [
      [0, 1, 0, 0, 0],
      [0, 0, 1, 0, 0],
      [0, 0, 0, 1, 0],
      [0, 0, 1, 0, 0],
      [0, 1, 0, 0, 0],
    ],
    clock: [
      [0, 1, 1, 1, 0],
      [1, 0, 1, 0, 1],
      [1, 0, 1, 1, 1],
      [1, 0, 0, 0, 1],
      [0, 1, 1, 1, 0],
    ],
    message: [
      [1, 1, 1, 1, 1],
      [1, 0, 0, 0, 1],
      [1, 0, 0, 0, 1],
      [1, 1, 1, 1, 1],
      [1, 0, 0, 0, 0],
    ],
    copy: [
      [1, 1, 1, 0, 0],
      [1, 0, 1, 0, 0],
      [1, 1, 1, 1, 1],
      [0, 0, 1, 0, 1],
      [0, 0, 1, 1, 1],
    ],
    "external-link": [
      [0, 1, 1, 1, 1],
      [0, 0, 0, 1, 1],
      [1, 1, 0, 1, 0],
      [1, 1, 0, 0, 0],
      [1, 1, 1, 0, 0],
    ],
    "shield-check": [
      [1, 1, 1, 1, 1],
      [1, 0, 0, 0, 1],
      [1, 0, 1, 0, 1],
      [0, 1, 1, 1, 0],
      [0, 0, 1, 0, 0],
    ],
    "shield-alert": [
      [1, 1, 1, 1, 1],
      [1, 0, 1, 0, 1],
      [1, 0, 1, 0, 1],
      [0, 1, 0, 1, 0],
      [0, 0, 1, 0, 0],
    ],
    building: [
      [1, 1, 1, 1, 1],
      [1, 0, 0, 0, 1],
      [1, 1, 1, 1, 1],
      [1, 0, 1, 0, 1],
      [1, 1, 1, 1, 1],
    ],
    "check-circle": [
      [0, 1, 1, 1, 0],
      [1, 0, 0, 0, 1],
      [1, 0, 1, 0, 1],
      [1, 1, 0, 0, 1],
      [0, 1, 1, 1, 0],
    ],
    "alert-circle": [
      [0, 1, 1, 1, 0],
      [1, 0, 1, 0, 1],
      [1, 0, 1, 0, 1],
      [1, 0, 0, 0, 1],
      [0, 1, 1, 1, 0],
    ],
    dollar: [
      [0, 0, 1, 0, 0],
      [0, 1, 1, 1, 0],
      [0, 0, 1, 0, 0],
      [0, 1, 1, 1, 0],
      [0, 0, 1, 0, 0],
    ],
    target: [
      [0, 1, 1, 1, 0],
      [1, 0, 0, 0, 1],
      [1, 0, 1, 0, 1],
      [1, 0, 0, 0, 1],
      [0, 1, 1, 1, 0],
    ],
    swords: [
      [0, 0, 0, 0, 1],
      [0, 1, 0, 1, 0],
      [0, 0, 1, 0, 0],
      [0, 1, 0, 1, 0],
      [1, 0, 0, 0, 0],
    ],
    quote: [
      [1, 1, 0, 0, 0],
      [1, 1, 0, 0, 0],
      [0, 0, 1, 1, 0],
      [0, 0, 1, 1, 0],
      [0, 0, 0, 0, 0],
    ],
    alert: [
      [0, 0, 1, 0, 0],
      [0, 1, 0, 1, 0],
      [0, 1, 1, 1, 0],
      [0, 1, 1, 1, 0],
      [0, 0, 0, 0, 0],
    ],
    "wifi-off": [
      [1, 0, 0, 0, 1],
      [0, 1, 1, 1, 0],
      [0, 1, 0, 1, 0],
      [0, 1, 1, 1, 0],
      [1, 0, 0, 0, 1],
    ],
    refresh: [
      [0, 1, 1, 1, 0],
      [1, 0, 0, 1, 0],
      [1, 0, 1, 0, 1],
      [0, 1, 0, 0, 1],
      [0, 1, 1, 1, 0],
    ],
    help: [
      [0, 1, 1, 1, 0],
      [0, 0, 0, 1, 0],
      [0, 0, 1, 0, 0],
      [0, 0, 0, 0, 0],
      [0, 0, 1, 0, 0],
    ],
    "chevron-up": [
      [0, 0, 0, 0, 0],
      [0, 0, 1, 0, 0],
      [0, 1, 0, 1, 0],
      [1, 0, 0, 0, 1],
      [0, 0, 0, 0, 0],
    ],
    "map-pin": [
      [0, 1, 1, 1, 0],
      [1, 0, 0, 0, 1],
      [1, 0, 1, 0, 1],
      [0, 1, 0, 1, 0],
      [0, 0, 1, 0, 0],
    ],
    upload: [
      [0, 0, 1, 0, 0],
      [0, 1, 1, 1, 0],
      [0, 0, 1, 0, 0],
      [0, 0, 1, 0, 0],
      [1, 1, 1, 1, 1],
    ],
    globe: [
      [0, 1, 1, 1, 0],
      [1, 0, 1, 0, 1],
      [1, 1, 1, 1, 1],
      [1, 0, 1, 0, 1],
      [0, 1, 1, 1, 0],
    ],
    info: [
      [0, 0, 1, 0, 0],
      [0, 0, 0, 0, 0],
      [0, 0, 1, 0, 0],
      [0, 0, 1, 0, 0],
      [0, 0, 1, 0, 0],
    ],
    grid: [
      [1, 1, 1, 1, 1],
      [1, 0, 0, 0, 1],
      [1, 1, 1, 1, 1],
      [1, 0, 0, 0, 1],
      [1, 1, 1, 1, 1],
    ],
    cpu: [
      [1, 0, 1, 0, 1],
      [0, 1, 1, 1, 0],
      [1, 1, 0, 1, 1],
      [0, 1, 1, 1, 0],
      [1, 0, 1, 0, 1],
    ],
    hash: [
      [0, 1, 0, 1, 0],
      [1, 1, 1, 1, 1],
      [0, 1, 0, 1, 0],
      [1, 1, 1, 1, 1],
      [0, 1, 0, 1, 0],
    ],
    wrench: [
      [0, 1, 1, 1, 0],
      [1, 0, 0, 0, 1],
      [1, 0, 0, 0, 1],
      [0, 1, 1, 1, 0],
      [0, 0, 1, 0, 0],
    ],
    flip: [
      [1, 1, 0, 0, 0],
      [1, 0, 0, 1, 1],
      [0, 0, 1, 0, 0],
      [1, 1, 0, 0, 1],
      [0, 0, 0, 1, 1],
    ],
  }

  const matrix = icons[name] || icons.plus || []

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${5 * gap + dotSize} ${5 * gap + dotSize}`}
      className={`${className} ${animate ? "animate-pulse" : ""}`}
      aria-hidden="true"
    >
      {matrix.map((row, y) =>
        row.map((cell, x) =>
          cell === 1 ? (
            <circle
              key={`${x}-${y}`}
              cx={x * gap + gap / 2 + dotSize / 2}
              cy={y * gap + gap / 2 + dotSize / 2}
              r={dotSize}
              fill="currentColor"
              className={animate ? `animate-pulse` : ""}
              style={animate ? { animationDelay: `${(x + y) * 50}ms` } : undefined}
            />
          ) : null,
        ),
      )}
    </svg>
  )
}
